import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UploadProgressContext = createContext(null);

export function UploadProgressProvider({ children }) {
  // Queue of documents: [{ planId, doc }]
  const [uploadQueue, setUploadQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  // Batch results: [{ planId, doc, status: 'complete'|'error'|'pending', detail }]
  const [batchResults, setBatchResults] = useState([]);
  // iter-54 — server-side batch job id so the Cancel button in the
  // floating tracker can call POST /batch-upload/{jobId}/cancel.
  // Null when the current run isn't a server-dispatched batch (e.g.
  // single-doc uploads) — the button hides in that case.
  const [batchJobId, setBatchJobId] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  // iter-105 — track when the user first hit Cancel so the UI can
  // surface a Force Dismiss escape hatch if the worker doesn't unwind.
  const [cancelRequestedAt, setCancelRequestedAt] = useState(null);
  const [dismissing, setDismissing] = useState(false);
  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const completedRef = useRef(false);
  const tokenRef = useRef(null);
  const onCompleteCallbackRef = useRef(null);

  const activeUpload = uploadQueue.length > 0 ? uploadQueue[currentIndex] || null : null;
  const isUploading = uploadQueue.length > 0;
  const batchTotal = uploadQueue.length;
  const batchCurrent = currentIndex + 1;

  // Register a callback for when any upload completes
  const onUploadComplete = useCallback((callback) => {
    onCompleteCallbackRef.current = callback;
  }, []);

  // Start tracking a single upload
  const startTracking = useCallback((planId, doc, token) => {
    completedRef.current = false;
    tokenRef.current = token;
    startTimeRef.current = Date.now();
    setProgress(null);
    setElapsed(0);
    setCurrentIndex(0);
    setUploadQueue([{ planId, doc }]);
    setBatchResults([]);
  }, []);

  // Start tracking a batch of uploads
  const startBatchTracking = useCallback((docs, token) => {
    // docs: [{ planId, doc }]
    completedRef.current = false;
    tokenRef.current = token;
    startTimeRef.current = Date.now();
    setProgress(null);
    setElapsed(0);
    setCurrentIndex(0);
    setUploadQueue(docs);
    setBatchResults([]);
  }, []);

  // Advance to next doc in queue
  const advanceToNext = useCallback(() => {
    setCurrentIndex(prev => {
      const next = prev + 1;
      if (next < uploadQueue.length) {
        completedRef.current = false;
        setProgress(null);
        return next;
      }
      return prev;
    });
  }, [uploadQueue.length]);

  // Stop all tracking
  const stopTracking = useCallback(() => {
    setUploadQueue([]);
    setCurrentIndex(0);
    setProgress(null);
    setElapsed(0);
    setBatchResults([]);
    setBatchJobId(null);
    setCancelling(false);
    setCancelRequestedAt(null);
    setDismissing(false);
    completedRef.current = false;
    clearInterval(pollRef.current);
    clearInterval(timerRef.current);
  }, []);

  // iter-54 — Cancel the running server-side batch. Idempotent —
  // safe to call multiple times; the endpoint no-ops if the job is
  // already terminal.
  const cancelBatch = useCallback(async () => {
    if (!batchJobId || cancelling) return { success: false, reason: 'no-op' };
    setCancelling(true);
    setCancelRequestedAt(Date.now());
    try {
      const token = sessionStorage.getItem('token') || tokenRef.current;
      const res = await axios.post(
        `${API_URL}/api/performcare/upload-center/batch-upload/${batchJobId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return { success: true, data: res.data };
    } catch (err) {
      setCancelling(false);
      setCancelRequestedAt(null);
      return { success: false, error: err };
    }
  }, [batchJobId, cancelling]);

  // iter-105 — Force Dismiss: unconditionally mark the job dismissed so
  // the tracker clears even if the worker is hung on Playwright. Super
  // admin only on the backend (POST /upload-center/dismiss-batch/{id}).
  const dismissBatch = useCallback(async () => {
    if (!batchJobId || dismissing) return { success: false, reason: 'no-op' };
    setDismissing(true);
    try {
      const token = sessionStorage.getItem('token') || tokenRef.current;
      const res = await axios.post(
        `${API_URL}/api/performcare/upload-center/dismiss-batch/${batchJobId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Fully unwind local state — the worker will land in whatever
      // terminal state it does, but the tracker should stop watching.
      stopTracking();
      return { success: true, data: res.data };
    } catch (err) {
      setDismissing(false);
      return { success: false, error: err };
    }
  }, [batchJobId, dismissing, stopTracking]);

  // Polling effect — watches current document
  useEffect(() => {
    const current = uploadQueue[currentIndex];
    if (!current?.planId || !tokenRef.current) return;
    const planId = current.planId;
    let active = true;
    completedRef.current = false;
    let consecutive401s = 0;

    const poll = async () => {
      if (!active || completedRef.current) return;
      // Read the token FRESH every poll — the global axios refresh
      // interceptor rotates ``sessionStorage.token`` silently, but a
      // captured-at-mount token would stay stale and keep producing
      // 401s.  This fixes the console flood seen when uploads run
      // longer than the 30-min JWT TTL.
      const currentToken = sessionStorage.getItem('token') || tokenRef.current;
      try {
        const res = await axios.get(
          `${API_URL}/api/performcare/upload-center/progress/${planId}`,
          { headers: { Authorization: `Bearer ${currentToken}` }, timeout: 8000 }
        );
        consecutive401s = 0;
        if (active && res.data && res.data.status !== 'not_found') {
          setProgress(res.data);
          if (res.data.status === 'complete' || res.data.status === 'error') {
            completedRef.current = true;

            // Record result
            setBatchResults(prev => [...prev, {
              planId,
              doc: current.doc,
              status: res.data.status,
              detail: res.data.detail || '',
            }]);

            // Fire callback
            if (onCompleteCallbackRef.current) {
              onCompleteCallbackRef.current(res.data);
            }

            // If more docs in queue, advance after a brief pause
            if (currentIndex + 1 < uploadQueue.length) {
              setTimeout(() => { if (active) advanceToNext(); }, 2000);
            } else {
              // All done — auto-dismiss after 12 seconds
              setTimeout(() => { if (active) stopTracking(); }, 12000);
            }
          }
        }
      } catch (err) {
        // Bail out after 3 consecutive 401s — the refresh interceptor
        // failed (refresh token also expired), so polling will never
        // recover without a re-login.  Quit cleanly instead of
        // hammering the API every 2s.
        if (err?.response?.status === 401) {
          consecutive401s += 1;
          if (consecutive401s >= 3 && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            active = false;
          }
        }
      }
    };

    poll();
    pollRef.current = setInterval(poll, 2000);
    return () => { active = false; clearInterval(pollRef.current); };
  }, [currentIndex, uploadQueue, advanceToNext, stopTracking]);

  // Elapsed timer
  useEffect(() => {
    if (!isUploading) {
      clearInterval(timerRef.current);
      return;
    }
    startTimeRef.current = startTimeRef.current || Date.now();
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [isUploading]);

  return (
    <UploadProgressContext.Provider value={{
      activeUpload, progress, elapsed,
      startTracking, startBatchTracking, stopTracking, onUploadComplete,
      isUploading, batchTotal, batchCurrent, batchResults,
      // iter-54
      batchJobId, setBatchJobId, cancelBatch, cancelling,
      // iter-105
      cancelRequestedAt, dismissBatch, dismissing,
    }}>
      {children}
    </UploadProgressContext.Provider>
  );
}

export function useUploadProgress() {
  const ctx = useContext(UploadProgressContext);
  if (!ctx) throw new Error('useUploadProgress must be used within UploadProgressProvider');
  return ctx;
}
